/**
 * Lecture des commentaires (« motifs ») des demandes de congé.
 *
 * Le champ FileMaker `commentaire` stocke une conversation sérialisée en JSON,
 * chaque entrée ayant pour clé l'auteur du message :
 *
 *   [{"user":"Voyage cameroun"}]
 *   [{"user":"Voyage cameroun"},{"admin":"Validé, bon voyage !"}]
 *
 * Les données existantes peuvent aussi contenir du texte brut, du JSON encodé
 * deux fois, un tableau déjà désérialisé, des retours chariot FileMaker non
 * échappés ou des guillemets typographiques. Toutes les fonctions de ce module
 * sont tolérantes : elles ne lèvent jamais d'exception.
 */

// Clés désignant l'employé à l'origine de la demande
const USER_ROLE_KEYS = ['user', 'utilisateur', 'employe', 'salarie', 'collaborateur', 'demandeur'];

// Clés désignant une réponse côté administration, avec leur libellé d'affichage
const ADMIN_ROLE_LABELS = {
  admin: 'Administrateur',
  administrateur: 'Administrateur',
  reponse: 'Administrateur',
  rh: 'RH',
  manager: 'Manager',
  responsable: 'Responsable',
  direction: 'Direction',
  gestionnaire: 'Gestionnaire',
  valideur: 'Valideur',
};

// Format « détaillé » : { role: "admin", message: "…", date: "…", nom: "…" }
const TEXT_KEYS = ['message', 'msg', 'text', 'texte', 'content', 'contenu', 'commentaire', 'comment', 'motif', 'body'];
const ROLE_KEYS = ['role', 'author', 'auteur', 'from', 'sender', 'emetteur', 'type'];
const DATE_KEYS = ['date', 'datetime', 'timestamp', 'ts', 'time', 'heure', 'horodatage', 'created_at', 'createdat', 'date_creation'];
const NAME_KEYS = ['name', 'nom', 'user_name', 'username', 'author_name', 'auteur_nom', 'prenom'];
const IGNORED_KEYS = ['id', 'user_id', 'id_user', 'id_demande', 'statut', 'status', 'lu', 'read', 'seen', 'vu'];

const MAX_DEPTH = 6;

/* ───────────────────────── Utilitaires internes ───────────────────────── */

function normalizeKey(key) {
  return String(key || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function isUserKey(key) {
  return USER_ROLE_KEYS.includes(normalizeKey(key));
}

function isKnownRoleKey(key) {
  const k = normalizeKey(key);
  return USER_ROLE_KEYS.includes(k) || Object.prototype.hasOwnProperty.call(ADMIN_ROLE_LABELS, k);
}

function isMetaKey(key) {
  const k = normalizeKey(key);
  return DATE_KEYS.includes(k) || NAME_KEYS.includes(k) || IGNORED_KEYS.includes(k) || ROLE_KEYS.includes(k);
}

function isFilled(value) {
  return value !== null && value !== undefined && value !== '';
}

// Nettoie un texte : retours chariot FileMaker (\r), espaces insécables, lignes vides multiples
function cleanText(value) {
  if (!isFilled(value) || typeof value === 'object') return '';
  return String(value)
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function looksLikeJson(str) {
  return /^[[{"“]/.test(str);
}

function tryJson(str) {
  try {
    return { ok: true, value: JSON.parse(str) };
  } catch (e) {
    return { ok: false };
  }
}

// Échappe les caractères de contrôle (retours chariot FileMaker…) présents dans les chaînes JSON
function escapeControlCharsInStrings(str) {
  let out = '';
  let inString = false;
  let escaped = false;
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (!inString) {
      if (ch === '"') inString = true;
      out += ch;
      continue;
    }
    if (escaped) {
      escaped = false;
      out += ch;
    } else if (ch === '\\') {
      escaped = true;
      out += ch;
    } else if (ch === '"') {
      inString = false;
      out += ch;
    } else if (ch.charCodeAt(0) < 0x20) {
      const code = ch.charCodeAt(0);
      if (ch === '\n') out += '\\n';
      else if (ch === '\r') out += '\\r';
      else if (ch === '\t') out += '\\t';
      else out += '\\u' + code.toString(16).padStart(4, '0');
    } else {
      out += ch;
    }
  }
  return out;
}

// Remplace les guillemets typographiques utilisés comme délimiteurs JSON
function normalizeQuotes(str) {
  return str.replace(/[“”„‟″]/g, '"');
}

const ESCAPES = { '"': '"', '\\': '\\', '/': '/', b: '', f: '', n: '\n', r: '\n', t: '\t' };

function unescapeJsonString(value) {
  return String(value).replace(/\\(u[0-9a-fA-F]{4}|[\s\S])/g, (match, code) => {
    if (code.length === 5) return String.fromCharCode(parseInt(code.slice(1), 16));
    return Object.prototype.hasOwnProperty.call(ESCAPES, code) ? ESCAPES[code] : code;
  });
}

/**
 * Désérialise une chaîne JSON de manière tolérante (gère le double encodage).
 * Renvoie la valeur désérialisée, ou la chaîne d'origine si ce n'est pas du JSON valide.
 */
function decode(str) {
  let value = str;
  for (let depth = 0; depth < 4 && typeof value === 'string'; depth++) {
    const s = value.trim();
    if (!s || !looksLikeJson(s)) return s;
    const attempts = [s, escapeControlCharsInStrings(s), escapeControlCharsInStrings(normalizeQuotes(s))];
    let parsed = null;
    for (const attempt of attempts) {
      const result = tryJson(attempt);
      if (result.ok) {
        parsed = result;
        break;
      }
    }
    if (!parsed) return s;
    value = parsed.value;
  }
  return value;
}

// Dernier recours pour du JSON invalide : extraction des paires "clé": "valeur"
const PAIR_RE = /["'“”‘’]?([A-Za-zÀ-ÿ_][\wÀ-ÿ-]*)["'“”‘’]?\s*:\s*(?:"([\s\S]*?)"(?=\s*[,}\]]|\s*$)|“([\s\S]*?)”|'([\s\S]*?)'(?=\s*[,}\]]|\s*$))/g;

function extractPairs(str) {
  const entries = [];
  let current = null;
  let match;
  PAIR_RE.lastIndex = 0;
  while ((match = PAIR_RE.exec(str)) !== null) {
    const key = match[1];
    const raw = match[2] !== undefined ? match[2] : match[3] !== undefined ? match[3] : match[4];
    const value = unescapeJsonString(raw || '');
    const k = normalizeKey(key);
    if (DATE_KEYS.includes(k) || NAME_KEYS.includes(k)) {
      // Métadonnée rattachée au message qui la précède
      if (current) current[key] = value;
      continue;
    }
    current = { [key]: value };
    entries.push(current);
  }
  return entries;
}

// Objet sans guillemets du type [{user:Voyage cameroun}]
function matchBareObject(str) {
  const match = str.match(/^\[?\s*\{\s*["'“”]?([A-Za-zÀ-ÿ_][\wÀ-ÿ-]*)["'“”]?\s*:\s*([\s\S]*?)\s*\}\s*\]?$/);
  if (!match) return null;
  const text = match[2].replace(/^["'“”]+|["'“”]+$/g, '');
  return { key: match[1], text };
}

// Équivalent de Array.prototype.flatMap (indisponible sur les anciennes versions de Node)
function flatMap(list, fn) {
  return list.reduce((acc, item) => acc.concat(fn(item)), []);
}

// Structure ressemblant à une conversation : objets portant une clé d'auteur ou de texte
function looksLikeConversation(value) {
  const items = Array.isArray(value) ? value : [value];
  return items.length > 0 && items.every((item) => item && typeof item === 'object' && !Array.isArray(item)
    && Object.keys(item).some((key) => isKnownRoleKey(key) || TEXT_KEYS.includes(normalizeKey(key))));
}

function startsLikeConversation(str) {
  const match = str.match(/^\[?\s*\{\s*["'“”]?([A-Za-zÀ-ÿ_][\wÀ-ÿ-]*)["'“”]?\s*:/);
  return !!match && (isKnownRoleKey(match[1]) || TEXT_KEYS.includes(normalizeKey(match[1])));
}

/**
 * Conversation enregistrée par erreur comme texte d'un message, par exemple
 * [{"user":"[{\"user\":\"Voyage\"}]"},{"admin":"OK"}] : on la « déballe ».
 * Renvoie null si le texte n'est pas une conversation.
 */
function decodeNestedConversation(value, defaultKey, depth) {
  if (typeof value !== 'string' || depth >= MAX_DEPTH) return null;
  const str = value.trim();
  if (!/^[[{]/.test(str)) return null;
  const decoded = decode(str);
  let nested = [];
  if (typeof decoded !== 'string') {
    if (looksLikeConversation(decoded)) nested = fromValue(decoded, defaultKey, depth + 1);
  } else if (startsLikeConversation(str)) {
    nested = flatMap(extractPairs(str), (pair) => fromObject(pair, defaultKey, depth + 1));
  }
  return nested.length ? nested : null;
}

function fromValue(value, defaultKey, depth) {
  if (depth > MAX_DEPTH || !isFilled(value)) return [];
  if (Array.isArray(value)) return flatMap(value, (item) => fromValue(item, defaultKey, depth + 1));
  if (typeof value === 'object') return fromObject(value, defaultKey, depth);
  const nested = decodeNestedConversation(value, defaultKey, depth);
  if (nested) return nested;
  const text = cleanText(value);
  return text ? [{ key: defaultKey, text }] : [];
}

function fromObject(obj, defaultKey, depth) {
  const entries = Object.entries(obj);
  if (!entries.length) return [];
  const find = (keys) => entries.find(([k, v]) => keys.includes(normalizeKey(k)) && isFilled(v));

  const dateEntry = find(DATE_KEYS);
  const nameEntry = find(NAME_KEYS);
  const meta = {
    date: dateEntry ? dateEntry[1] : null,
    author: nameEntry ? cleanText(nameEntry[1]) : '',
  };

  // Format détaillé : { role: "admin", message: "…" }
  const textEntry = find(TEXT_KEYS);
  if (textEntry && typeof textEntry[1] !== 'object') {
    const roleEntry = find(ROLE_KEYS);
    let key = roleEntry && typeof roleEntry[1] === 'string' ? roleEntry[1] : '';
    if (!key) {
      // { admin: "Christian", message: "…" } : la clé de rôle porte le nom de l'auteur
      const roleKeyEntry = entries.find(([k]) => isKnownRoleKey(k));
      if (roleKeyEntry) {
        key = roleKeyEntry[0];
        if (!meta.author) meta.author = cleanText(roleKeyEntry[1]);
      }
    }
    const text = cleanText(textEntry[1]);
    return text ? [{ ...meta, key: key || defaultKey, text }] : [];
  }

  // Format compact : { user: "…" } (voire { user: "…", admin: "…" })
  const messages = [];
  entries.forEach(([k, v]) => {
    if (isMetaKey(k) || !isFilled(v)) return;
    if (typeof v === 'object') {
      // { admin: { message: "…", date: "…" } }
      fromValue(v, k, depth + 1).forEach((m) => messages.push({ ...meta, ...m }));
      return;
    }
    const nested = decodeNestedConversation(v, k, depth);
    if (nested) {
      nested.forEach((m) => messages.push(m));
      return;
    }
    const text = cleanText(v);
    if (text) messages.push({ ...meta, key: k, text });
  });
  return messages;
}

function parseRaw(raw) {
  if (!isFilled(raw)) return [];
  if (typeof raw !== 'string') return fromValue(raw, 'user', 0);

  const str = raw.trim();
  if (!str || /^(null|undefined)$/i.test(str)) return [];

  const decoded = decode(str);
  if (typeof decoded !== 'string') return fromValue(decoded, 'user', 0);

  if (looksLikeJson(decoded)) {
    const pairs = extractPairs(decoded);
    if (pairs.length) return flatMap(pairs, (pair) => fromObject(pair, 'user', 1));
    const bare = matchBareObject(decoded);
    if (bare) {
      const text = cleanText(bare.text);
      return text ? [{ key: bare.key, text }] : [];
    }
  }

  // Texte brut (anciens enregistrements)
  const text = cleanText(decoded);
  return text ? [{ key: 'user', text }] : [];
}

function parseDate(value) {
  const empty = { date: null, hasTime: false, dateText: '' };
  if (!isFilled(value)) return empty;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? empty : { date: value, hasTime: true, dateText: '' };
  }

  const str = String(value).trim();

  // Timestamp en secondes ou millisecondes
  if (/^\d{9,13}$/.test(str)) {
    let n = Number(str);
    if (n < 1e12) n *= 1000;
    const d = new Date(n);
    return isNaN(d.getTime()) ? { ...empty, dateText: str } : { date: d, hasTime: true, dateText: '' };
  }

  // ISO : 2026-10-09, 2026-10-09 14:17, 2026-10-09T14:17:00Z
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/);
  if (m) {
    if (m[7]) {
      const withZone = new Date(str.replace(' ', 'T'));
      if (!isNaN(withZone.getTime())) return { date: withZone, hasTime: true, dateText: '' };
    }
    const d = new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
    if (!isNaN(d.getTime())) return { date: d, hasTime: m[4] !== undefined, dateText: '' };
  }

  // Français JJ/MM/AAAA [HH:MM[:SS]] (ou format FileMaker MM/JJ/AAAA si le jour dépasse 12)
  m = str.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) {
    let day = +m[1];
    let month = +m[2];
    if (month > 12 && day <= 12) [day, month] = [month, day];
    const d = new Date(+m[3], month - 1, day, +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
    if (!isNaN(d.getTime()) && d.getMonth() === month - 1) {
      return { date: d, hasTime: m[4] !== undefined, dateText: '' };
    }
  }

  return { ...empty, dateText: str };
}

function humanizeKey(key) {
  const label = String(key || '').replace(/[_-]+/g, ' ').trim();
  if (!label) return '';
  if (label.length <= 3) return label.toUpperCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/* ───────────────────────────── API publique ───────────────────────────── */

/**
 * Transforme la valeur brute du champ `commentaire` en liste de messages :
 * [{ id, role: 'user' | 'admin', key, label, text, author, date, hasTime, dateText, rawDate }]
 */
export function parseCommentaire(raw) {
  let list;
  try {
    list = parseRaw(raw);
  } catch (e) {
    list = [];
  }
  return list.map((m, index) => {
    const role = isUserKey(m.key) ? 'user' : 'admin';
    const { date, hasTime, dateText } = parseDate(m.date);
    const normalized = normalizeKey(m.key);
    return {
      id: index,
      role,
      key: m.key,
      label: role === 'user' ? 'Employé' : ADMIN_ROLE_LABELS[normalized] || humanizeKey(m.key),
      text: m.text,
      author: m.author || '',
      date,
      hasTime,
      dateText,
      // Valeur d'origine, réécrite telle quelle par appendMessage
      rawDate: isFilled(m.date) && typeof m.date !== 'object' ? m.date : '',
    };
  });
}

/**
 * Vue d'ensemble d'une conversation : le motif (premier message de l'employé)
 * et les réponses qui suivent.
 */
export function getConversation(raw) {
  const messages = parseCommentaire(raw);
  const motifMessage = messages.find((m) => m.role === 'user') || messages[0] || null;
  const replies = messages.filter((m) => m !== motifMessage);
  return {
    messages,
    motif: motifMessage ? motifMessage.text : '',
    motifMessage,
    replies,
    replyCount: replies.length,
    lastMessage: messages.length ? messages[messages.length - 1] : null,
    hasAdminReply: messages.some((m) => m.role === 'admin'),
  };
}

/** Texte du motif seul (chaîne vide si aucun). */
export function getMotif(raw) {
  return getConversation(raw).motif;
}

/** Tous les messages concaténés en texte simple (exports, commentaires libres…). */
export function commentaireToText(raw, separator = '\n') {
  return parseCommentaire(raw)
    .map((m) => m.text)
    .join(separator);
}

/** Date d'un message formatée en français : « 09/10/2026 à 14:17 ». */
export function formatMessageDate(message) {
  if (!message) return '';
  if (message.date) {
    const day = message.date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    if (!message.hasTime) return day;
    const time = message.date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${day} à ${time}`;
  }
  return message.dateText || '';
}

/* ─────────────────────────── Écriture de messages ─────────────────────────── */

function pad(n) {
  return String(n).padStart(2, '0');
}

/** Date locale au format ISO 8601 avec fuseau : « 2026-10-09T14:52:00+02:00 ». */
export function toLocalIsoString(date = new Date()) {
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    + `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/**
 * Nouveau message au format stocké dans FileMaker :
 * { "admin": "Peux-tu décaler d'une semaine ?", "nom": "Christian", "date": "2026-10-09T14:52:00+02:00" }
 */
export function createMessage(role, text, { nom = '', date = new Date() } = {}) {
  const entry = { [role === 'admin' ? 'admin' : 'user']: cleanText(text) };
  if (cleanText(nom)) entry.nom = cleanText(nom);
  entry.date = toLocalIsoString(date);
  return entry;
}

// Message lu → entrée au format stocké (normalise les anciens formats)
function toEntry(message) {
  const entry = { [message.key || message.role]: message.text };
  if (message.author) entry.nom = message.author;
  if (isFilled(message.rawDate)) {
    entry.date = message.rawDate;
  } else if (message.date) {
    const iso = toLocalIsoString(message.date);
    entry.date = message.hasTime ? iso : iso.slice(0, 10);
  } else if (message.dateText) {
    entry.date = message.dateText;
  }
  return entry;
}

/** Ajoute un message à une conversation et renvoie le nouveau JSON (format normalisé). */
export function appendMessage(raw, entry) {
  const entries = parseCommentaire(raw).map(toEntry);
  entries.push(entry);
  return JSON.stringify(entries);
}
